<template>
  <div class="card card-graph flex-fill">
    <div class="card-header d-flex justify-content-between">
      <h5 class="card-title mb-0">{{ title }}</h5>
      <div class="d-flex align-items-center gap-2">
        <button
          v-if="!historyLink"
          type="button"
          class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
          @click="resetZoom"
        >
          <i class="bx bx-reset"></i>
          Reset
        </button>
        <router-link
          v-if="historyLink"
          :to="{ name: 'analytics-dashboard-component', params: { deviceId: devId, componentname: 'cpu-utilization' } }"
          class="btn btn-primary btn-sm d-flex align-items-center gap-1"
        >
          <i class="bx bx-history"></i>
          History
        </router-link>
      </div>
    </div>
    <div class="card-body">
      <div class="graph d-flex flex-wrap flex-col justify-content-center" ref="graphRef" :style="[{ minHeight: historyLink ? '300px' : '500px' }]">
        <div ref="selectionRef" class="zoom-selection-box"></div>
        <div class="d-flex flex-col justify-content-center" ref="charRef" :style="[{ height: historyLink ? '290px' : '490px', width: '100%' }]"></div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.graph {
  /* min-height: 300px; */
  min-width: 200px;
  align-items: top;
  position: relative;
}

.zoom-selection-box {
  position: absolute;
  display: none;
  top: 0;
  height: 100%;
  border: 1px solid rgba(37, 99, 235, 0.7);
  background: rgba(37, 99, 235, 0.15);
  pointer-events: none;
  z-index: 2;
}
</style>

<script>
import { onMounted, onUnmounted, onBeforeMount, onBeforeUnmount, ref, computed, watch } from 'vue'
import axios from 'axios'
import * as echarts from 'echarts'
import { useStore } from 'vuex'
import { getChartOptions } from '@/data/linechart.cpu'
import { createQuery } from '@/data/createCUQuery'
import {
  applyDataPointSymbols,
  buildDrillDownRange,
  clearChartHoverState,
  DRILL_DOWN_LOADING,
  setTooltipInteraction,
  tooltipTriggerOn,
} from '@/data/chartDrillDown'

export default {
  name: 'CPU Utilization',
  props: {
    host: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },

    refresh: {
      type: Number,
      default: 30,
    },
    title: {
      type: String,
      default: 'CPU Utilization',
    },
    cpuCount: {
      type: Number,
      default: 4,
    },
    devId: {
      type: Number,
      default: 0,
    },
    historyLink: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    //const orgID = '1b0f79e17233c082'
    const timer = ref(null)
    const chart = ref(null)

    const charRef = ref(null)
    const graphRef = ref(null)
    const selectionRef = ref(null)
    const graphValue = ref(0)

    const dataArray = ref({})

    const store = useStore()

    const layoutMode = computed(() => store.state.layout.layoutMode)
    const apiKey = computed(() => store.state.auth.user?.accessToken)
    const apiHost = computed(() => window.apiHost)
    //const _updateIncrement = computed(() => this.updateIncrement)
    //const apiOrgId = computed(() => store.state.config.apiOrgId);

    const strDurationStart = ref('-60m')
    const strDurationEnd = ref('0m')
    const strInterval = ref('5m')
    const dragStartX = ref(null)
    const dragCurrentX = ref(null)
    const zoomRange = ref({ min: null, max: null })
    const isDrillDown = ref(false)
    const tooltipEnabled = ref(true)
    const tooltipEnableTimer = ref(null)

    const resizeObserver = new ResizeObserver(() => {
      onWindowResize()
    })

    function clearHoverState() {
      clearChartHoverState(chart.value)
    }

    function scheduleTooltipEnable() {
      if (tooltipEnableTimer.value !== null) {
        clearTimeout(tooltipEnableTimer.value)
      }

      tooltipEnableTimer.value = setTimeout(() => {
        tooltipEnabled.value = true
        setTooltipInteraction(chart.value, true)
        tooltipEnableTimer.value = null
      }, 120)
    }

    function setTooltipEnabled(enabled) {
      tooltipEnabled.value = enabled

      if (tooltipEnableTimer.value !== null) {
        clearTimeout(tooltipEnableTimer.value)
        tooltipEnableTimer.value = null
      }

      setTooltipInteraction(chart.value, enabled)
    }

    function updateSelectionBox() {
      if (!selectionRef.value || dragStartX.value === null || dragCurrentX.value === null) {
        return
      }

      const left = Math.min(dragStartX.value, dragCurrentX.value)
      const width = Math.abs(dragCurrentX.value - dragStartX.value)
      selectionRef.value.style.display = width > 0 ? 'block' : 'none'
      selectionRef.value.style.left = `${left}px`
      selectionRef.value.style.width = `${width}px`
    }

    function clearSelectionBox() {
      dragStartX.value = null
      dragCurrentX.value = null

      if (selectionRef.value) {
        selectionRef.value.style.display = 'none'
        selectionRef.value.style.width = '0px'
      }
    }

    function getGridBounds() {
      if (!chart.value) {
        return null
      }

      const gridModel = chart.value.getModel().getComponent('grid')
      const coordinateSystem = gridModel && gridModel.coordinateSystem
      return coordinateSystem ? coordinateSystem.getRect() : null
    }

    function onChartMouseDown(event) {
      if (props.historyLink || !chart.value) {
        return
      }

      const gridBounds = getGridBounds()
      if (!gridBounds) {
        return
      }

      const { offsetX, offsetY } = event
      const withinGrid = offsetX >= gridBounds.x && offsetX <= gridBounds.x + gridBounds.width && offsetY >= gridBounds.y && offsetY <= gridBounds.y + gridBounds.height

      if (!withinGrid) {
        clearSelectionBox()
        return
      }

      dragStartX.value = offsetX
      dragCurrentX.value = offsetX
      updateSelectionBox()
    }

    function onChartMouseMove(event) {
      if (dragStartX.value === null) {
        return
      }

      dragCurrentX.value = event.offsetX
      updateSelectionBox()
    }

    async function onChartMouseUp() {
      if (!chart.value || dragStartX.value === null || dragCurrentX.value === null) {
        clearSelectionBox()
        return
      }

      const startPixel = dragStartX.value
      const endPixel = dragCurrentX.value
      clearSelectionBox()

      if (Math.abs(endPixel - startPixel) < 8) {
        return
      }

      const startValue = chart.value.convertFromPixel({ xAxisIndex: 0 }, Math.min(startPixel, endPixel))
      const endValue = chart.value.convertFromPixel({ xAxisIndex: 0 }, Math.max(startPixel, endPixel))

      if (startValue == null || endValue == null) {
        return
      }

      const range = buildDrillDownRange(startValue, endValue)
      if (!range) {
        return
      }

      strDurationStart.value = range.start
      strDurationEnd.value = range.stop
      strInterval.value = range.interval
      zoomRange.value = { min: null, max: null }
      isDrillDown.value = true
      setTooltipEnabled(false)
      clearHoverState()
      chart.value.showLoading(DRILL_DOWN_LOADING)
      await main()
    }

    function bindHistoryDragZoom() {
      if (!chart.value || props.historyLink) {
        return
      }

      const zr = chart.value.getZr()
      zr.off('mousedown', onChartMouseDown)
      zr.off('mousemove', onChartMouseMove)
      zr.off('mouseup', onChartMouseUp)
      zr.off('globalout', clearSelectionBox)

      zr.on('mousedown', onChartMouseDown)
      zr.on('mousemove', onChartMouseMove)
      zr.on('mouseup', onChartMouseUp)
      zr.on('globalout', clearSelectionBox)
    }

    function resetZoom() {
      if (!chart.value) {
        return
      }

      isDrillDown.value = false
      zoomRange.value = { min: null, max: null }
      strDurationStart.value = store.state.graph.graphDurationStart
      strDurationEnd.value = store.state.graph.graphDurationEnd
      strInterval.value = store.state.graph.graphInterval
      clearSelectionBox()
      setTooltipEnabled(false)
      clearHoverState()
      chart.value.showLoading(DRILL_DOWN_LOADING)
      main()
    }

    // Watch for changes in layoutMode
    watch(layoutMode, (newMode) => {
      if (chart.value !== null) {
        const theme = newMode === 'dark' ? 'dark' : 'light'
        chart.value.dispose()
        chart.value = echarts.init(charRef.value, theme)
        drawChart(graphValue.value, dataArray.value)
      }
    })

    const graphUpdateCount = computed(() => store.state.graph.graphUpdateCount)
    watch(graphUpdateCount, () => {
      //alert(`Graph update count changed to ${newCount}`)
      const _strDurationStart = computed(() => store.state.graph.graphDurationStart)
      const _strDurationEnd = computed(() => store.state.graph.graphDurationEnd)
      const _strInterval = computed(() => store.state.graph.graphInterval)

      strDurationEnd.value = _strDurationEnd.value
      strDurationStart.value = _strDurationStart.value
      strInterval.value = _strInterval.value
      zoomRange.value = { min: null, max: null }
      isDrillDown.value = false
      setTooltipEnabled(false)
      clearHoverState()
      chart.value.showLoading({ text: 'Loading data, please wait...', color: '#4f6bed', textColor: '#000', maskColor: 'rgba(255, 255, 255, 0.2)', zlevel: 0 })
      main()
    })

    async function drawChart(value, data) {
      graphValue.value = value
      var option = getChartOptions(props.cpuCount, data)
      //option.series[0].data[0].value = value;
      //option.series[0].data[0].name = txtTitle.value;
      //option.series[0].title.fontSize = 22;
      applyDataPointSymbols(option, isDrillDown.value, chart.value ? chart.value.getWidth() : 0)
      option.backgroundColor = layoutMode.value === 'dark' ? '#22354e' : '#ffffff'
      option.xAxis.min = zoomRange.value.min
      option.xAxis.max = zoomRange.value.max
      option.tooltip.triggerOn = tooltipTriggerOn(tooltipEnabled.value)
      chart.value.setOption(option, { replaceMerge: ['series', 'graphic'] })
      bindHistoryDragZoom()
      clearHoverState()
      setTimeout(() => {
        clearHoverState()
      }, 0)
      chart.value.hideLoading()
      scheduleTooltipEnable()
    }

    function initChart() {
      if (chart.value === null) {
        var theme = 'light'
        if (layoutMode.value === 'dark') {
          theme = 'dark'
        }
        chart.value = echarts.init(charRef.value, theme)
      }

      var option = getChartOptions(props.cpuCount, {})
      option.backgroundColor = layoutMode.value === 'dark' ? '#22354e' : '#ffffff'
      option.xAxis.min = zoomRange.value.min
      option.xAxis.max = zoomRange.value.max
      option.tooltip.triggerOn = tooltipTriggerOn(tooltipEnabled.value)
      chart.value.setOption(option, { replaceMerge: ['series', 'graphic'] })
      bindHistoryDragZoom()
      clearHoverState()
    }

    async function main() {
      try {
        //var terms = props.terms

        const query = createQuery(props.host, props.type, strDurationStart.value, strDurationEnd.value, strInterval.value)
        //console.log(query)
        const response = await axios.post(apiHost.value + '/core/api/v1/analytics/query', query, {
          headers: {
            Authorization: 'Bearer ' + apiKey.value,
            'Content-Type': 'application/vnd.flux',
            Accept: 'application/csv',
          },
        })

        var data = response.data
        var lines = -1
        var value = 0
        var dataArrCache = []
        var dataArrAvail = []
        var dataArrUsed = []
        var dataArrayBuffer = []
        data.split('\n').forEach((line) => {
          if (++lines > 0) {
            var fields = line.trim().split(',')
            //console.log(fields)
            if (fields[7] === 'cpu_idle') dataArrCache.push([fields[5], fields[6]])
            else if (fields[7] == 'cpu_interrupt') dataArrAvail.push([fields[5], fields[6]])
            else if (fields[7] == 'cpu_system') dataArrUsed.push([fields[5], fields[6]])
            else if (fields[7] == 'cpu_user') dataArrayBuffer.push([fields[5], fields[6]])
            else if (fields[11] === 'idle') dataArrCache.push([fields[5], fields[6]])
            else if (fields[11] == 'interrupt') dataArrAvail.push([fields[5], fields[6]])
            else if (fields[11] == 'user') dataArrUsed.push([fields[5], fields[6]])
            else if (fields[11] == 'system') dataArrayBuffer.push([fields[5], fields[6]])
          }
        })

        dataArray.value = {
          cache: dataArrCache,
          total: dataArrAvail,
          used: dataArrUsed,
          buffer: dataArrayBuffer,
        }

        drawChart(value, dataArray.value)
      } catch (error) {
        console.error(error)
      }
    }

    function onWindowResize() {
      if (chart.value !== null) {
        chart.value.resize()
      }
    }

    onMounted(() => {
      initChart()
      main()

      if (props.refresh > 0) {
        timer.value = setInterval(() => {
          main()
        }, props.refresh * 1000)
      }

      if (graphRef.value) {
        resizeObserver.observe(graphRef.value)
      }
    })

    onUnmounted(() => {
      if (props.refresh > 0 && timer.value !== null) {
        clearInterval(timer.value)
      }
      if (tooltipEnableTimer.value !== null) {
        clearTimeout(tooltipEnableTimer.value)
      }
    })

    onBeforeMount(() => {
      window.addEventListener('resize', onWindowResize)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', onWindowResize)
      resizeObserver.unobserve(graphRef.value)
    })

    return {
      charRef,
      graphRef,
      selectionRef,
      resetZoom,
    }
  },
}
</script>
